import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { useEnabledAppsOptions } from '@components/newNavigation/hooks';
import { ErrorTypes } from '@fabric-ui/components';
import { emptyObject, QueryKey } from '@fabric-ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@utils/AvRouter';
import { getAppIdFromPathName, getPathNameByAccount } from '@utils/router.utils';
import useHandleError from '../hooks/useHandleError';
import { useTypeNameMap } from '../hooks/UseTypeNameMap';
import { APP_PATHS, getRoleNameByValue, ROLES } from '../types';
import { sharedWorkerService } from '../workers/sharedWorkerService';
import { useAuthContext } from './AuthContextProvider';
import { hasMoreThanOneAppInPayingAccount, useHasAppInAccount } from './AvContext.hooks';
import { AccountType } from './AvContext.types';
import { GET_UI_CONFIG } from './gql';
import { DataContextType } from './types';
import { useAccount } from './useAccount';
import { useCalcDims } from './useCalcDims';
import useEntities from './useEntities';
import { useMetrics } from './useMetrics';
import { useForceRender } from './utils';

export const DataContext = React.createContext<DataContextType>({
  UIConfig: undefined,
  accountData: {
    id: '',
    name: '',
    apps: [],
    logo: null,
    accountType: AccountType.POC,
    autoScheduleHour: null,
    expiresAt: null,
    flags: { isRecommendedFieldsEnabled: false },
    aiFeatures: { enabled: true },
    customerFacing: false,
  },
  accountEntities: {} as DataContextType['accountEntities'],
  enabledApps: [],
  getPathName: () => ({ to: '', params: {} }),
  hasAppInAccount: () => false,
  hasMultipleAppsInPayingAccount: false,
  isLoading: true,
  measurements: { all: [], visible: [], asObject: emptyObject },
  calculatedDimensions: { all: [], visible: [], asObject: emptyObject },
  refetchAccountData() {},
  refetchUIConfig() {},
  selectedApp: '',
  setAccountId() {},
  setSelectedApp() {},
  typeNameMap: undefined,
});

const useUiConfig = (options?) =>
  useGqlQuery({
    queryKey: [QueryKey.UiConfig],
    query: GET_UI_CONFIG,
    ...options,
    retry: 10,
    retryDelay: 1000,
  });

export const DataContextProvider = ({ children }: React.PropsWithChildren) => {
  const { accountId, user, userPermissions, isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const handleError = useHandleError();
  const client = useQueryClient();
  const selectedAppId = getAppIdFromPathName(location.pathname) || '';
  const [selectedApp, setSelectedApp] = useState(selectedAppId || '');

  const forceRender = useForceRender();

  const {
    data: accountData,
    refetch: refetchAccountData,
    isPending: isLoadingAccount,
    error: accountError,
  } = useAccount({ enabled: isAuthenticated });

  useEffect(() => {
    if (selectedAppId) {
      setSelectedApp(selectedAppId);
    }
  }, [selectedAppId]);

  const previousAccount = useRef(accountId);

  useEffect(() => {
    if (previousAccount.current !== accountId) {
      switchAccountClearCache(accountId);
      forceRender();
    }

    const handleBeforeUnload = () => {
      client.clear();
      globalThis.isReloading = true;
      sharedWorkerService.notifyWorkerOnUnloadEvent(accountId);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener on component unmount or when accountId changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [accountId]);

  const switchAccountClearCache = id => {
    sharedWorkerService.notifyWorkerOnSwitchAccountEvent(previousAccount.current);
    setSelectedApp('');
    previousAccount.current = id;
    client.clear();
    navigate({ to: '/$accountId', params: { accountId: `${id}` }, ignoreBlocker: true });
  };

  const setAccountId = id => {
    if (window.location.pathname === '/') {
      navigate({ to: '/$accountId', params: { accountId: `${id}` }, ignoreBlocker: true });
    } else {
      switchAccountClearCache(id);
    }
  };

  const {
    data: UIConfig = emptyObject,
    isPending: isLoadingUIConfig,
    refetch: refetchUIConfig,
    error: uiConfigError,
  } = useUiConfig({ enabled: isAuthenticated });
  const { data: typeNameMap, isPending: isLoadingSources, error: connectorsError } = useTypeNameMap({ enabled: isAuthenticated });
  const {
    data: measurements = { all: [], visible: [], asObject: emptyObject },
    isPending: isLoadingMetrics,
    error: metricsError,
  } = useMetrics({ enabled: isAuthenticated });

  const {
    data: calculatedDimensions = { all: [], visible: [], asObject: emptyObject },
    isPending: isLoadingCalcDims,
    error: calcDimsError,
  } = useCalcDims({ enabled: isAuthenticated });

  const { data: accountEntities, isLoading: loadingFields, error: entitiesError } = useEntities({ enabled: isAuthenticated });
  const hasAppInAccount = useHasAppInAccount({ accountData });

  const hasMultipleAppsInPayingAccount = hasMoreThanOneAppInPayingAccount({ accountData });

  const enabledApps = useEnabledAppsOptions({ accountData, hasAllowedPermission: userPermissions?.hasAllowedPermission });

  useEffect(() => {
    const setInitialSelectedApp = () => {
      const userAppsNames = enabledApps.map(app => app.id);
      const userFavorites = user?.favoriteApps.map(app => app.toLowerCase()).filter(app => userAppsNames.includes(app));
      const appListForInitialSelection = userFavorites?.length > 0 ? userFavorites : userAppsNames;
      const userApps = appListForInitialSelection.find(id => userAppsNames.includes(id));
      setSelectedApp(userApps);
    };

    if (user?.roleName && user.roleName === getRoleNameByValue(ROLES.PRE_POV)) {
      setSelectedApp(APP_PATHS.PLATFORM);
    } else if (accountData.apps.length && !selectedApp && user?.roleName) {
      setInitialSelectedApp();
    }
  }, [accountData.apps, user.roleName, enabledApps, accountId]);

  const error = accountError || entitiesError || uiConfigError || connectorsError || metricsError || calcDimsError;
  if (error) {
    handleError(error);
    console.error(ErrorTypes.AvContext, error);
    throw new Error(ErrorTypes.AvContext);
  }

  const value = useMemo(
    () => ({
      setAccountId,
      selectedApp,
      setSelectedApp,
      isLoading: isLoadingMetrics || isLoadingCalcDims || isLoadingAccount || isLoadingUIConfig || isLoadingSources || loadingFields,
      accountEntities,
      typeNameMap,
      measurements,
      calculatedDimensions,
      accountData,
      refetchAccountData,
      UIConfig,
      getPathName: getPathNameByAccount(accountId, selectedApp),
      refetchUIConfig,
      hasAppInAccount,
      hasMultipleAppsInPayingAccount,
      enabledApps,
    }),
    [
      loadingFields,
      isLoadingAccount,
      accountEntities,
      typeNameMap,
      measurements,
      calculatedDimensions,
      isLoadingUIConfig,
      accountData,
      UIConfig,
      selectedApp,
      enabledApps,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useDataContext = () => useContext(DataContext);
